/**
 * Canonical admin tab list — ADM-07 / ADM-22 (UI/UX Phase 2, Stage 2).
 *
 * ADMIN_TAB_IDS is the single source of truth every admin navigation
 * surface derives from, so the five surfaces can never silently drift
 * apart again (the PR #1426 failure shape):
 *
 *  1. The adminTab state type in useApplications.ts IS `AdminTabId` —
 *     a strongly-typed navigation to any tab below type-checks, with
 *     no `as any` cast.
 *  2. AdminDashboard's nav:change acceptance check is `isAdminTabId`
 *     (it used to re-declare this list inline as `validAdminTabs`).
 *  3. The sidebar structure (lib/adminNavStructure.ts) types its entry
 *     ids against `AdminTabId` and is asserted a SUBSET of this list
 *     by tests/adminNavStructure.test.ts: exactly one valid tab holds
 *     no sidebar home — 'bulk-jobs' (a Batch Center sub-surface
 *     addressed via /admin?tab=bulk-jobs). ('senders' used to be the
 *     second; ADM-08 / decision D4 retired it as a standalone tab —
 *     Email Senders' one home is the Workspace Settings sub-tab, and
 *     old ?tab=senders links land there.)
 *  4. AdminDashboard's render chain carries one explicit branch per
 *     id below except 'applicants', which is the chain's fallback —
 *     the census test pins that split.
 *  5. App.tsx's non-admin URL guard, ADMIN_ONLY_PATH_SEGMENTS, is
 *     DERIVED from this list (below), never re-declared.
 *
 * Adding a tab means adding its id here (plus its render branch and,
 * if it needs one, its sidebar entry); the parity census in
 * tests/adminNavStructure.test.ts fails until every surface agrees.
 */
export const ADMIN_TAB_IDS = [
  // ── Flat sidebar block: everyday pages ─────────────────────────────
  'dashboard',
  'applicants',
  'approvals',
  'documents',
  'signing',
  'tickets',
  'finance',
  'reports',
  'my-events',
  'events',
  // ── Jobs area (one sidebar entry; page carries the tab bar) ───────
  'jobs',
  'offers',
  'invitations',
  // ── Templates group ────────────────────────────────────────────────
  'templates',
  'doc-templates',
  // ── Organization group ─────────────────────────────────────────────
  'organizations',
  'referrers',
  'sponsors',
  'employers',
  // ── Orchestration group ────────────────────────────────────────────
  'workflow',
  'automations',
  'batch',
  'ai-suggestions',
  // ── Account / workspace administration ─────────────────────────────
  'users',
  'settings',
  'workspaces',
  // ── Valid tabs with NO sidebar home (see header, surface 3) ───────
  'bulk-jobs',
] as const;

export type AdminTabId = (typeof ADMIN_TAB_IDS)[number];

/** Type guard: is this value one of the canonical admin tab ids? */
export function isAdminTabId(value: unknown): value is AdminTabId {
  return typeof value === 'string' && (ADMIN_TAB_IDS as readonly string[]).includes(value);
}

/**
 * Admin tabs whose first URL path segment is NOT admin-only, each with
 * the routing fact that keeps it out of the non-admin bounce guard in
 * App.tsx (getTabFromPathSegment). Every other admin tab's segment
 * bounces a non-admin to their own dashboard.
 */
export const ADMIN_PATH_SEGMENT_EXCLUSIONS: readonly AdminTabId[] = [
  // The guard's own bounce target; every role's home segment.
  'dashboard',
  // Employers share this segment — App.tsx role-splits 'applicants'
  // (and its 'applications' alias) between admin and employer shells.
  'applicants',
  // Jobs-area segments the employer shell routes as its own tabs
  // (My Jobs, Sent Offers, Invitations).
  'jobs',
  'offers',
  'invitations',
  // The sponsor shell carries its own 'employers' view under this
  // segment (Casework), so the segment is not admin-only.
  'employers',
  // Calendar segments exist in every role's shell.
  'events',
  'my-events',
  // Not a routed top-level segment: no navigation surface generates
  // /bulk-jobs (the sidebar has no entry); the surface is addressed
  // via /admin?tab=bulk-jobs from Batch Center.
  'bulk-jobs',
];

/**
 * URL sections that exist ONLY in the admin/team shell. A non-admin who
 * signs in with one of these left in the address bar (e.g. /approvals
 * from a previous admin session in the same tab) must land on their own
 * dashboard — the segment must never become their active tab, or the
 * admin URL sticks as route residue over the correct portal.
 *
 * Derived from ADMIN_TAB_IDS minus the shared segments above (ADM-22:
 * this derivation is what adds 'documents', 'automations', 'reports'
 * and 'senders', the four real admin tabs the hand-maintained list had
 * dropped; 'senders' has since been retired as a tab by ADM-08, so it
 * no longer derives a segment either). Widened to string[] because
 * App.tsx compares raw URL segments against it.
 */
export const ADMIN_ONLY_PATH_SEGMENTS: readonly string[] = ADMIN_TAB_IDS.filter(
  (id) => !ADMIN_PATH_SEGMENT_EXCLUSIONS.includes(id),
);
